import React from 'react'
import { Row, Col, Button } from 'reactstrap'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Menu,
  MenuItem,
  ListItemText,
  Badge,
} from '@mui/material'
import MuiButton from '@mui/material/Button'
import FilterListIcon from '@mui/icons-material/FilterList'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import createPlotlyComponent from 'react-plotly.js/factory'
import Plotly from 'plotly.js-basic-dist-min'
import moment from 'moment'

const Plot = createPlotlyComponent(Plotly)

const ALL_WEEKDAYS = ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su']

const RESOLUTIONS = [
  { value: 'day', label: 'Päivä' },
  { value: 'week', label: 'Viikko' },
  { value: 'month', label: 'Kuukausi' },
]

const METRICS = [
  { value: 'percent', label: 'Varausaste %' },
  { value: 'cost', label: 'Myynti €' },
  { value: 'occupancy', label: 'Varatut tunnit' },
]

const COLORS = {
  primary: '#0b8043',
  primaryFill: 'rgba(11, 128, 67, 0.12)',
  comparison: '#ea4335',
  comparisonFill: 'rgba(234, 67, 53, 0.06)',
  grid: 'rgba(0,0,0,0.06)',
  gridDash: 'dot',
  text: '#5f6368',
  bg: '#fff',
}

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

const denseMenuItemSx = { py: 0.25, minHeight: 28 }
const denseCheckboxSx = { p: 0.25 }

const ChangeIcon = ({ change }) => {
  if (change > 0) return <ArrowDropUpIcon style={{ color: 'green' }} />
  if (change < 0) return <ArrowDropDownIcon style={{ color: 'red' }} />
  return <></>
}

var tableToExcel = (function () {
  var uri = 'data:application/vnd.ms-excel;base64,',
    template =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>{table}</table></body></html>',
    base64 = function (s) {
      return window.btoa(unescape(encodeURIComponent(s)))
    },
    format = function (s, c) {
      return s.replace(/{(\w+)}/g, function (m, p) {
        return c[p]
      })
    }
  return function (table, name) {
    if (!table.nodeType) table = document.getElementsByClassName(table)[0]
    var ctx = { worksheet: name || 'Worksheet', table: table.innerHTML }
    window.location.href = uri + base64(format(template, ctx))
  }
})()

const filterData = (rows, { categories, courts, weekdays }) => {
  if (!rows || rows.length === 0) return []
  let filtered = rows
  if (categories.length > 0) {
    filtered = filtered.filter((r) => categories.includes(r.category))
  }
  if (courts.length > 0) {
    filtered = filtered.filter((r) => courts.includes(r.court))
  }
  if (weekdays.length > 0 && weekdays.length < 7) {
    filtered = filtered.filter((r) => weekdays.includes(r.day))
  }
  return filtered
}

const generateAllKeys = (startdate, enddate, resolution) => {
  const keys = []
  const start = moment(startdate, 'YYYY-MM-DD')
  const end = moment(enddate, 'YYYY-MM-DD')
  if (resolution === 'day') {
    const d = start.clone()
    while (d.isSameOrBefore(end, 'day')) {
      keys.push(d.format('YYYY-MM-DD'))
      d.add(1, 'day')
    }
  } else if (resolution === 'week') {
    const d = start.clone().startOf('isoWeek')
    while (d.isSameOrBefore(end, 'day')) {
      keys.push(d.isoWeekYear() + '-W' + String(d.isoWeek()).padStart(2, '0'))
      d.add(1, 'week')
    }
  } else {
    const d = start.clone().startOf('month')
    while (d.isSameOrBefore(end, 'day')) {
      keys.push(d.format('YYYY-MM'))
      d.add(1, 'month')
    }
  }
  return keys
}

const aggregateByResolution = (rows, resolution, startdate, enddate) => {
  if (!rows || rows.length === 0) return []
  const groups = {}
  rows.forEach((row) => {
    let key
    if (resolution === 'day') {
      key = row.dayIndex
    } else if (resolution === 'week') {
      const m = moment(row.dayIndex, 'YYYY-MM-DD')
      key = m.isoWeekYear() + '-W' + String(m.isoWeek()).padStart(2, '0')
    } else {
      key = moment(row.dayIndex, 'YYYY-MM-DD').format('YYYY-MM')
    }
    if (!groups[key]) groups[key] = { cost: 0, occupancy: 0, windowLength: 0 }
    groups[key].cost += parseFloat(row.cost)
    groups[key].occupancy += parseFloat(row.occupancy)
    groups[key].windowLength += row.windowLength
  })
  const allKeys = startdate && enddate
    ? generateAllKeys(startdate, enddate, resolution)
    : Object.keys(groups).sort()
  return allKeys.map((key) => {
    const g = groups[key] || { cost: 0, occupancy: 0, windowLength: 0 }
    const percent =
      g.windowLength > 0 ? (g.occupancy / g.windowLength) * 100 : 0
    const avrPrice = g.occupancy > 0 ? g.cost / g.occupancy : 0
    return {
      label: key,
      cost: g.cost,
      occupancy: g.occupancy,
      windowLength: g.windowLength,
      percent,
      avrPrice,
    }
  })
}

class BusyVisual extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      categories: [],
      courts: [],
      weekdays: [...ALL_WEEKDAYS],
      resolution: 'day',
      metric: 'percent',
      anchorCategories: null,
      anchorCourts: null,
      anchorWeekdays: null,
      anchorResolution: null,
      anchorMetric: null,
    }
  }

  getUniqueValues = (field) => {
    const rows = this.props.rows || []
    const values = [...new Set(rows.map((r) => r[field]).filter(Boolean))]
    return values.sort()
  }

  getMetricLabel = () => {
    const m = METRICS.find((m) => m.value === this.state.metric)
    return m ? m.label : ''
  }

  handleCategoryToggle = (cat) => {
    this.setState((prev) => {
      const categories = prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat]
      return { categories }
    })
  }

  handleCourtToggle = (court) => {
    this.setState((prev) => {
      const courts = prev.courts.includes(court)
        ? prev.courts.filter((c) => c !== court)
        : [...prev.courts, court]
      return { courts }
    })
  }

  handleWeekdayToggle = (day) => {
    this.setState((prev) => {
      const weekdays = prev.weekdays.includes(day)
        ? prev.weekdays.filter((d) => d !== day)
        : [...prev.weekdays, day]
      return { weekdays }
    })
  }

  render() {
    const { rows, comparisonrows, comparison, startdate, enddate, comparisonstartdate, comparisonenddate } = this.props
    const { categories, courts, weekdays, resolution, metric } = this.state

    const filtered = filterData(rows, { categories, courts, weekdays })
    const aggregated = aggregateByResolution(filtered, resolution, startdate, enddate)

    let comparisonAggregated = []
    if (comparison && comparisonrows && comparisonrows.length > 0) {
      const compFiltered = filterData(comparisonrows, {
        categories,
        courts,
        weekdays,
      })
      comparisonAggregated = aggregateByResolution(compFiltered, resolution, comparisonstartdate, comparisonenddate)
    }

    const availableCategories = this.getUniqueValues('category')
    const availableCourts = this.getUniqueValues('court')

    const useIndexAxis = comparison && comparisonAggregated.length > 0
    const traces = []
    if (aggregated.length > 0) {
      traces.push({
        x: useIndexAxis
          ? aggregated.map((_, i) => i)
          : aggregated.map((d) => d.label),
        y: aggregated.map((d) => d[metric]),
        customdata: aggregated.map((d) => d.label),
        type: 'scatter',
        mode: 'lines',
        name: 'Jakso',
        line: {
          color: COLORS.primary,
          width: 2.5,
          shape: 'linear',
        },
        fill: 'tozeroy',
        fillcolor: COLORS.primaryFill,
        fillgradient: {
          type: 'vertical',
          colorscale: [
            [0, 'rgba(11, 128, 67, 0)'],
            [1, 'rgba(11, 128, 67, 0.18)'],
          ],
        },
        hovertemplate: '<b>%{customdata}</b><br>%{y:.1f}<extra></extra>',
      })
    }
    if (useIndexAxis) {
      traces.push({
        x: comparisonAggregated.map((_, i) => i),
        y: comparisonAggregated.map((d) => d[metric]),
        customdata: comparisonAggregated.map((d) => d.label),
        type: 'scatter',
        mode: 'lines',
        name: 'Vertailu',
        line: {
          color: COLORS.comparison,
          width: 1.5,
          dash: 'dot',
          shape: 'linear',
        },
        fill: 'tozeroy',
        fillcolor: COLORS.comparisonFill,
        fillgradient: {
          type: 'vertical',
          colorscale: [
            [0, 'rgba(234, 67, 53, 0)'],
            [1, 'rgba(234, 67, 53, 0.15)'],
          ],
        },
        hovertemplate: '<b>%{customdata}</b><br>%{y:.1f}<extra></extra>',
      })
    }

    const hasData = rows && rows.length > 0

    const chartLayout = {
      autosize: true,
      height: 500,
      margin: { l: 50, r: 16, t: 8, b: 44 },
      yaxis: {
        title: {
          text: this.getMetricLabel(),
          font: { size: 12, color: COLORS.text },
          standoff: 8,
        },
        gridcolor: COLORS.grid,
        griddash: COLORS.gridDash,
        gridwidth: 1,
        zeroline: false,
        tickfont: { size: 11, color: COLORS.text },
        showline: false,
      },
      xaxis: {
        tickfont: { size: 11, color: COLORS.text },
        tickangle: aggregated.length > 20 ? -45 : 0,
        gridcolor: COLORS.grid,
        griddash: COLORS.gridDash,
        gridwidth: 1,
        showline: false,
        showgrid: false,
        ...(useIndexAxis
          ? {
              tickvals: aggregated.map((_, i) => i),
              ticktext: aggregated.map((d) => d.label),
            }
          : {}),
      },
      legend: {
        orientation: 'h',
        y: -0.15,
        x: 0.5,
        xanchor: 'center',
        font: { size: 12, color: COLORS.text },
      },
      plot_bgcolor: COLORS.bg,
      paper_bgcolor: COLORS.bg,
      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: 'rgba(255,255,255,0.95)',
        bordercolor: 'rgba(0,0,0,0.1)',
        font: { size: 13, color: '#202124', family: 'inherit' },
      },
    }

    const chartConfig = {
      responsive: true,
      displaylogo: false,
      displayModeBar: false,
    }

    return (
      <React.Fragment>
        {hasData && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, marginTop: 10, alignItems: 'center' }}>
              <Badge color='primary' variant='dot' invisible={categories.length === 0}>
                <MuiButton
                  size='small'
                  variant='outlined'
                  onClick={(e) => this.setState({ anchorCategories: e.currentTarget })}
                  startIcon={<FilterListIcon fontSize='small' />}
                  style={{ textTransform: 'none', fontSize: 12 }}
                >
                  Kategoriat{categories.length > 0 ? ` (${categories.length})` : ''}
                </MuiButton>
              </Badge>
              <Menu
                anchorEl={this.state.anchorCategories}
                open={Boolean(this.state.anchorCategories)}
                onClose={() => this.setState({ anchorCategories: null })}
              >
                {availableCategories.map((cat) => (
                  <MenuItem key={cat} dense sx={denseMenuItemSx} onClick={() => this.handleCategoryToggle(cat)}>
                    <Checkbox size='small' sx={denseCheckboxSx} checked={categories.includes(cat)} />
                    <ListItemText primary={capitalize(cat)} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
                {categories.length > 0 && (
                  <MenuItem dense sx={denseMenuItemSx} onClick={() => this.setState({ categories: [] })}>
                    <ListItemText primary='Tyhjennä' primaryTypographyProps={{ color: 'error', fontSize: 13 }} />
                  </MenuItem>
                )}
              </Menu>

              <Badge color='primary' variant='dot' invisible={courts.length === 0}>
                <MuiButton
                  size='small'
                  variant='outlined'
                  onClick={(e) => this.setState({ anchorCourts: e.currentTarget })}
                  startIcon={<FilterListIcon fontSize='small' />}
                  style={{ textTransform: 'none', fontSize: 12 }}
                >
                  Kentät{courts.length > 0 ? ` (${courts.length})` : ''}
                </MuiButton>
              </Badge>
              <Menu
                anchorEl={this.state.anchorCourts}
                open={Boolean(this.state.anchorCourts)}
                onClose={() => this.setState({ anchorCourts: null })}
              >
                {availableCourts.map((court) => (
                  <MenuItem key={court} dense sx={denseMenuItemSx} onClick={() => this.handleCourtToggle(court)}>
                    <Checkbox size='small' sx={denseCheckboxSx} checked={courts.includes(court)} />
                    <ListItemText primary={capitalize(court)} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
                {courts.length > 0 && (
                  <MenuItem dense sx={denseMenuItemSx} onClick={() => this.setState({ courts: [] })}>
                    <ListItemText primary='Tyhjennä' primaryTypographyProps={{ color: 'error', fontSize: 13 }} />
                  </MenuItem>
                )}
              </Menu>

              <Badge color='primary' variant='dot' invisible={weekdays.length === ALL_WEEKDAYS.length}>
                <MuiButton
                  size='small'
                  variant='outlined'
                  onClick={(e) => this.setState({ anchorWeekdays: e.currentTarget })}
                  style={{ textTransform: 'none', fontSize: 12 }}
                >
                  Viikonpäivät{weekdays.length < ALL_WEEKDAYS.length ? ` (${weekdays.length}/7)` : ''}
                </MuiButton>
              </Badge>
              <Menu
                anchorEl={this.state.anchorWeekdays}
                open={Boolean(this.state.anchorWeekdays)}
                onClose={() => this.setState({ anchorWeekdays: null })}
              >
                {ALL_WEEKDAYS.map((day) => (
                  <MenuItem key={day} dense sx={denseMenuItemSx} onClick={() => this.handleWeekdayToggle(day)}>
                    <Checkbox size='small' sx={denseCheckboxSx} checked={weekdays.includes(day)} />
                    <ListItemText primary={day} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
              </Menu>

              <span style={{ borderLeft: '1px solid #e0e0e0', height: 24, margin: '0 4px' }} />

              <MuiButton
                size='small'
                variant='outlined'
                onClick={(e) => this.setState({ anchorResolution: e.currentTarget })}
                style={{ textTransform: 'none', fontSize: 12 }}
              >
                {RESOLUTIONS.find((r) => r.value === resolution)?.label || 'Päivä'}
              </MuiButton>
              <Menu
                anchorEl={this.state.anchorResolution}
                open={Boolean(this.state.anchorResolution)}
                onClose={() => this.setState({ anchorResolution: null })}
              >
                {RESOLUTIONS.map((r) => (
                  <MenuItem
                    key={r.value}
                    dense
                    sx={denseMenuItemSx}
                    selected={resolution === r.value}
                    onClick={() => this.setState({ resolution: r.value, anchorResolution: null })}
                  >
                    <ListItemText primary={r.label} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
              </Menu>

              <MuiButton
                size='small'
                variant='outlined'
                onClick={(e) => this.setState({ anchorMetric: e.currentTarget })}
                style={{ textTransform: 'none', fontSize: 12 }}
              >
                {METRICS.find((m) => m.value === metric)?.label || 'Varausaste %'}
              </MuiButton>
              <Menu
                anchorEl={this.state.anchorMetric}
                open={Boolean(this.state.anchorMetric)}
                onClose={() => this.setState({ anchorMetric: null })}
              >
                {METRICS.map((m) => (
                  <MenuItem
                    key={m.value}
                    dense
                    sx={denseMenuItemSx}
                    selected={metric === m.value}
                    onClick={() => this.setState({ metric: m.value, anchorMetric: null })}
                  >
                    <ListItemText primary={m.label} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                ))}
              </Menu>
            </div>

            {aggregated.length > 0 && (
              <Row>
                <Col>
                  <div style={{
                    borderBottom: '1px solid #e0e0e0',
                    padding: '4px 0 0',
                  }}>
                    <Plot
                      data={traces}
                      layout={chartLayout}
                      useResizeHandler
                      style={{ width: '100%' }}
                      config={chartConfig}
                    />
                  </div>
                </Col>
              </Row>
            )}

            <Row style={{ marginTop: 10 }}>
              <Col>
                <Button
                  size='sm'
                  onClick={() => tableToExcel('busyvisual-table')}
                >
                  Export to Excel
                </Button>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table className='busyvisual-table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Jakso</TableCell>
                      <TableCell>Summa €</TableCell>
                      <TableCell>Varatut tunnit / max</TableCell>
                      <TableCell>Varausaste %</TableCell>
                      <TableCell>Keskihinta/h</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregated.map((row, i) => {
                      const compRow =
                        comparison && comparisonAggregated[i]
                          ? comparisonAggregated[i]
                          : null
                      return (
                        <TableRow key={row.label}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell>
                            {compRow && (
                              <ChangeIcon change={row.cost - compRow.cost} />
                            )}
                            {row.cost.toFixed(2)} €
                            {compRow && (
                              <span style={{ fontSize: 'smaller' }}>
                                {' '}
                                ({compRow.cost.toFixed(2)} €{' '}
                                {(row.cost - compRow.cost).toFixed(2)})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {compRow && (
                              <ChangeIcon
                                change={row.occupancy - compRow.occupancy}
                              />
                            )}
                            {row.occupancy} / {row.windowLength}
                            {compRow && (
                              <span style={{ fontSize: 'smaller' }}>
                                {' '}
                                ({compRow.occupancy} / {compRow.windowLength}{' '}
                                {row.occupancy - compRow.occupancy})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {compRow && (
                              <ChangeIcon
                                change={row.percent - compRow.percent}
                              />
                            )}
                            {row.percent.toFixed(1)} %
                            {compRow && (
                              <span style={{ fontSize: 'smaller' }}>
                                {' '}
                                ({compRow.percent.toFixed(1)} %{' '}
                                {(row.percent - compRow.percent).toFixed(1)})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {compRow && (
                              <ChangeIcon
                                change={row.avrPrice - compRow.avrPrice}
                              />
                            )}
                            {row.avrPrice.toFixed(2)} €
                            {compRow && (
                              <span style={{ fontSize: 'smaller' }}>
                                {' '}
                                ({compRow.avrPrice.toFixed(2)} €{' '}
                                {(row.avrPrice - compRow.avrPrice).toFixed(2)})
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Col>
            </Row>
          </>
        )}
      </React.Fragment>
    )
  }
}

export default BusyVisual
